import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faBook } from '@fortawesome/free-solid-svg-icons';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';

// Definitions + FAQ education modal, shaped like the app's
// CgKnowledgeBaseModal (definitions[] + faqs[]). Copy adapted from
// Embed/MethodologyContent.jsx and the supplier chase help modal.
// Module-scope (not exported/reused elsewhere) — built as functions of `t`,
// called via useMemo below.
const buildDefinitions = (t) => [
  {
    title: t('freeSupplier:basicsModal.seeTitle', 'SEE — Specific Embedded Emissions'),
    content: t(
      'freeSupplier:basicsModal.seeContent',
      'The CO₂e emitted per tonne of your product. Direct SEE comes from fuels and processes at your installation; indirect SEE from the electricity you consume (consumption × grid emission factor).',
    ),
  },
  {
    title: t('freeSupplier:basicsModal.sefaTitle', 'SEFA — Specific Embedded Free Allocation'),
    content: t(
      'freeSupplier:basicsModal.sefaContent',
      'The free-allocation deduction your customers can claim: EU benchmark × CBAM factor. Chargeable emissions = SEE − SEFA, so a calculated SEFA directly lowers the CBAM cost of buying from you. Calculated on Premium.',
    ),
  },
  {
    title: t('freeSupplier:basicsModal.cbamCostTitle', 'CBAM cost'),
    content: t(
      'freeSupplier:basicsModal.cbamCostContent',
      'Chargeable emissions × the CBAM certificate price (tracks the EU ETS price). Iron & steel and aluminium goods are charged on Direct SEE only; other sectors on Total SEE.',
    ),
  },
  {
    title: t('freeSupplier:basicsModal.cnCodeTitle', 'CN code'),
    content: t(
      'freeSupplier:basicsModal.cnCodeContent',
      'The EU Combined Nomenclature code identifying your good in customs declarations — declarants report emissions per CN code.',
    ),
  },
  {
    title: t(
      'freeSupplier:basicsModal.productionMethodTitle',
      'Production method & qualifying parameters',
    ),
    content: t(
      'freeSupplier:basicsModal.productionMethodContent',
      'The EU-defined production route for your good (e.g. "Basic oxygen steelmaking - P35") plus its qualifying parameters (e.g. manganese content, scrap usage) from the CBAM communication template.',
    ),
  },
  {
    title: t('freeSupplier:basicsModal.electricitySourceTitle', 'Electricity source (SOE codes)'),
    content: t(
      'freeSupplier:basicsModal.electricitySourceContent',
      'How your installation sources electricity: SOE01 direct technical link to a generator, SOE02 power purchase agreement, SOE03 received from the grid. Determines which emission factor applies to indirect emissions.',
    ),
  },
  {
    title: t('freeSupplier:basicsModal.registryIdTitle', 'CBAM Registry ID'),
    content: t(
      'freeSupplier:basicsModal.registryIdContent',
      'The unique identifier of your installation in the EU CBAM Registry. Declarants use it to reference your installation in their declarations.',
    ),
  },
  {
    title: t('freeSupplier:basicsModal.verificationTitle', 'Verification'),
    content: t(
      'freeSupplier:basicsModal.verificationContent',
      'From the definitive period, embedded-emissions data must be verified by an accredited verifier. Unverified data may be discounted by your customers. Verification support is a Premium feature.',
    ),
  },
];

const buildFaqs = (t) => [
  {
    question: t(
      'freeSupplier:basicsModal.faqWhyAskingQuestion',
      'Why do my customers keep asking for this data?',
    ),
    answer: t(
      'freeSupplier:basicsModal.faqWhyAskingAnswer',
      "EU importers (declarants) must report the embedded emissions of every CBAM good they import — per CN code, installation and reporting period. If you don't provide actual data, they fall back to unfavorable EU default values.",
    ),
  },
  {
    question: t(
      'freeSupplier:basicsModal.faqDirectOnlyQuestion',
      'What happens if I only share Direct SEE?',
    ),
    answer: t(
      'freeSupplier:basicsModal.faqDirectOnlyAnswer',
      'For iron & steel and aluminium the chargeable basis is Direct SEE, but declarants still need the indirect figures and electricity details for their reports — incomplete records create follow-up requests.',
    ),
  },
  {
    question: t(
      'freeSupplier:basicsModal.faqEveryPeriodQuestion',
      'Do I need to fill every reporting period?',
    ),
    answer: t(
      'freeSupplier:basicsModal.faqEveryPeriodAnswer',
      'Declarants report quarterly, so they will ask for data covering the current period. Keeping the latest year published avoids repeated one-off requests.',
    ),
  },
];

function Faq({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 border-0 text-left text-xs font-semibold text-slate-700 transition-colors"
      >
        {question}
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className="text-slate-400 text-[10px] shrink-0"
        />
      </button>
      {open && <p className="text-xs text-slate-600 leading-relaxed px-3 py-2.5 m-0">{answer}</p>}
    </div>
  );
}

export default function CbamBasicsModal({ open, onOpenChange }) {
  const { t } = useTranslation('freeSupplier');
  const definitions = useMemo(() => buildDefinitions(t), [t]);
  const faqs = useMemo(() => buildFaqs(t), [t]);

  return (
    <CgDialog
      open={open}
      onOpenChange={onOpenChange}
      width={620}
      title={t('basicsModal.title', 'CBAM basics')}
      description={t('basicsModal.description', "The terms behind the fields you're filling in.")}
      footer={
        <div className="flex justify-end">
          <CgRadixButton
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            {t('basicsModal.closeButton', 'Close')}
          </CgRadixButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faBook} />
            {t('basicsModal.definitionsHeading', 'Definitions')}
          </p>
          <div className="flex flex-col gap-2.5">
            {definitions.map((d) => (
              <div key={d.title}>
                <p className="text-xs font-semibold text-slate-800 m-0">{d.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed m-0 mt-0.5">{d.content}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            {t('basicsModal.frequentlyAskedHeading', 'Frequently asked')}
          </p>
          <div className="flex flex-col gap-2">
            {faqs.map((f) => (
              <Faq
                key={f.question}
                {...f}
              />
            ))}
          </div>
        </div>
      </div>
    </CgDialog>
  );
}
